import { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import {
  DateRange,
  ClinicFinancialItem,
  PaginationInfo,
} from "../../types/transactionHistory.types";

interface ClinicListTableProps {
  dateRange: DateRange;
  onClinicClick: (clinic: ClinicFinancialItem) => void;
}

function formatCurrency(amount: number): string {
  if (amount >= 100000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const PAGE_SIZE = 10;

export default function ClinicListTable({ dateRange, onClinicClick }: ClinicListTableProps) {
  const [clinics, setClinics] = useState<ClinicFinancialItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");

  const fetchClinics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await TransactionHistoryService.getClinicsFinancialListing({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page,
        pageSize: PAGE_SIZE,
        searchText: searchText || undefined,
      });
      if (res.success) {
        setClinics(res.data || []);
        setPagination(res.pagination || null);
      }
    } catch (err) {
      console.error("Error fetching clinics:", err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, page, searchText]);

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  useEffect(() => {
    setPage(1);
  }, [dateRange, searchText]);

  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItemsCount ?? 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800">My Clinics</h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search clinics..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg w-48 focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary/50 transition-all"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/80">
                {["Clinic", "Status", "Gross", "Net", "Online", "Cash", "Returns", "Expenses", "Deposits"].map((h) => (
                  <th key={h} className="py-3 px-4 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 9 }).map((_, j) => (
                      <td key={j} className="py-3 px-4">
                        <div className="h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : clinics.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-sm text-gray-400">
                    No clinics found
                  </td>
                </tr>
              ) : (
                clinics.map((clinic) => (
                  <tr
                    key={clinic.clinicId}
                    onClick={() => onClinicClick(clinic)}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer group"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2.5">
                        {clinic.profilePicture ? (
                          <img
                            src={clinic.profilePicture}
                            alt={clinic.clinicName}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center text-xs font-bold text-secondary">
                            {clinic.clinicName?.charAt(0) || "C"}
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-800">{clinic.clinicName}</p>
                          <p className="text-[10px] text-gray-400">{clinic.city}, {clinic.state}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full
                        ${clinic.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
                      >
                        {clinic.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs font-semibold text-gray-800">
                      {formatCurrency(clinic.gross?.totalAmount || 0)}
                    </td>
                    <td className="py-3 px-4 text-xs font-semibold text-emerald-600">
                      {formatCurrency(clinic.net?.totalAmount || 0)}
                    </td>
                    <td className="py-3 px-4 text-xs text-blue-600">
                      {formatCurrency(clinic.online || 0)}
                    </td>
                    <td className="py-3 px-4 text-xs text-green-600">
                      {formatCurrency(clinic.cash || 0)}
                    </td>
                    <td className="py-3 px-4 text-xs text-red-500">
                      {formatCurrency(clinic.returns?.totalAmount || 0)}
                    </td>
                    <td className="py-3 px-4 text-xs text-orange-600">
                      {formatCurrency(clinic.expenses?.totalAmount || 0)}
                    </td>
                    <td className="py-3 px-4 text-xs text-indigo-600 flex items-center gap-1">
                      {formatCurrency(clinic.bankDeposits?.totalAmount || 0)}
                      <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing {clinics.length} of {totalItems} clinics
            </p>
            <div className="flex items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
                let pageNum: number;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 text-xs font-medium rounded-lg transition-colors
                      ${pageNum === page ? "bg-secondary text-white" : "text-gray-600 hover:bg-gray-100"}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
